import { createLogger } from './logger';
import { isTokenExpired } from './jwt';

const log = createLogger('api');

/**
 * Base URLs for the three apps.
 *
 * - `LIGHTNING_API_BASE` falls back to the legacy `VITE_API_BASE` env var
 *   so existing builds and any external scripts keep working unchanged.
 * - `VIDEO_API_BASE` is set to `/api/video` for `make deploy` builds and
 *   to `${LIGHTNING_API_BASE}/api/video` for CI deploys.
 * - `INTERIOR_API_BASE` follows the same pattern with `/api/interior`.
 *
 * All three apps now ship as a single backend service — the video and
 * interior FastAPI apps are mounted in-process at `/api/video/*` and
 * `/api/interior/*` inside `lightning.server` — so the SPA only needs
 * the lightning host plus a path prefix to reach the sub-app routes.
 * Both sub-app vars can only be empty when a developer runs
 * `npm run dev` / `make web-dev` without setting them in their `.env`;
 * in that case `isVideoEnabled()` / `isInteriorEnabled()` returns
 * false and the corresponding tab is hidden so the SPA doesn't surface
 * a dead link.
 */
export const LIGHTNING_API_BASE =
  import.meta.env.VITE_LIGHTNING_API_BASE ||
  import.meta.env.VITE_API_BASE ||
  '';
export const VIDEO_API_BASE = import.meta.env.VITE_VIDEO_API_BASE || '';
export const INTERIOR_API_BASE =
  import.meta.env.VITE_INTERIOR_API_BASE || '';
export const ADS_API_BASE = import.meta.env.VITE_ADS_API_BASE || '';

/**
 * Base URL for the decoupled "API wrapper" service (apigen). It runs as its
 * own process, so unlike the other sub-apps it is NOT mounted in-process.
 * Defaults to `/apigen`, which the Vite dev proxy forwards (with the prefix
 * stripped) to the local apigen server; official builds set
 * `VITE_APIGEN_API_BASE` to the deployed service URL.
 */
export const APIGEN_API_BASE = import.meta.env.VITE_APIGEN_API_BASE || '/apigen';

/** Optional server-to-server key sent as `X-API-Key` to the apigen service. */
export const APIGEN_API_KEY = import.meta.env.VITE_APIGEN_API_KEY || '';

/** Back-compat alias for callers written before the split. */
export const API_BASE = LIGHTNING_API_BASE;

export type ApiApp = 'lightning' | 'video' | 'interior' | 'ads' | 'api';

/**
 * True in every official deployment (CI + `make deploy` always set
 * `VITE_VIDEO_API_BASE`). The check only gates the Video tab in
 * dev-mode setups where the env var wasn't configured.
 */
export function isVideoEnabled(): boolean {
  return Boolean(VIDEO_API_BASE);
}

/**
 * Same dev-mode guard as `isVideoEnabled` but for the interior sub-app.
 * `make deploy` and CI always set `VITE_INTERIOR_API_BASE`.
 */
export function isInteriorEnabled(): boolean {
  return Boolean(INTERIOR_API_BASE);
}

/**
 * Same dev-mode guard pattern, applied to the ads sub-app. `make
 * deploy` and CI always set `VITE_ADS_API_BASE`; in local `npm run
 * dev` setups the Ads tab is hidden until the env var is configured.
 */
export function isAdsEnabled(): boolean {
  return Boolean(ADS_API_BASE);
}

/**
 * The API-wrapper section is always available (its base defaults to
 * `/apigen`). Kept as a helper for symmetry with the other sub-apps.
 */
export function isApiEnabled(): boolean {
  return Boolean(APIGEN_API_BASE);
}

const TOKEN_STORAGE_KEY = 'idp_token';

/**
 * Window event dispatched whenever we detect that the current session is no
 * longer valid (token expired locally, or the API rejected us with 401/403).
 *
 * `AuthProvider` listens for this and triggers a logout, which sends the user
 * back to the login screen instead of leaving the UI in a stuck "connected"
 * state with stale data — this is what used to require a manual logout +
 * Ctrl-Shift-R after the tab sat overnight.
 */
export const AUTH_EXPIRED_EVENT = 'auth:expired';

export interface AuthExpiredDetail {
  reason: string;
}

function dispatchAuthExpired(reason: string): void {
  log.info(`Session invalid (${reason}); requesting logout`);
  window.dispatchEvent(
    new CustomEvent<AuthExpiredDetail>(AUTH_EXPIRED_EVENT, {
      detail: { reason },
    })
  );
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export class SessionExpiredError extends Error {
  constructor(reason: string) {
    super(`Session expired (${reason}). Please sign in again.`);
    this.name = 'SessionExpiredError';
  }
}

export interface ApiFetchInit extends RequestInit {
  /**
   * Which backend to target. Defaults to `'lightning'` so the existing
   * lightning callers keep working unchanged. Pass `'video'` for the
   * 360-video service or `'interior'` for the 360-interior service.
   */
  app?: ApiApp;
}

/**
 * Centralized fetch wrapper for all backend API calls.
 *
 *  - Prepends the appropriate base URL (`LIGHTNING_API_BASE`,
 *    `VIDEO_API_BASE` or `INTERIOR_API_BASE`) for relative paths.
 *    Callers can also pass full URLs.
 *  - Attaches `Authorization: Bearer <idp_token>` when present.
 *  - Refuses to send a request whose token is already expired and signals the
 *    auth provider to log the user out instead.
 *  - On 401/403 from the backend, fires the same logout signal so the UI
 *    immediately reflects the invalid session.
 */
export async function apiFetch(
  input: string,
  init: ApiFetchInit = {}
): Promise<Response> {
  const token = getStoredToken();

  if (token && isTokenExpired(token)) {
    dispatchAuthExpired('token-expired-preflight');
    throw new SessionExpiredError('token-expired-preflight');
  }

  const headers = new Headers(init.headers);
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  let base: string;
  switch (init.app) {
    case 'video':
      base = VIDEO_API_BASE;
      break;
    case 'interior':
      base = INTERIOR_API_BASE;
      break;
    case 'ads':
      base = ADS_API_BASE;
      break;
    case 'api':
      base = APIGEN_API_BASE;
      break;
    default:
      base = LIGHTNING_API_BASE;
  }
  const url = /^https?:\/\//i.test(input) ? input : `${base}${input}`;
  const { app: _app, ...fetchInit } = init;
  const response = await fetch(url, { ...fetchInit, headers });

  if (response.status === 401 || response.status === 403) {
    dispatchAuthExpired(`http-${response.status}`);
  }

  return response;
}
